import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Download, FileText, ImageIcon, Lock, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { ID_SAMPLES } from "../../data/content";

const iconFor = (type) => {
  if (type === "live") return <Play size={16} />;
  if (type === "image") return <ImageIcon size={16} />;
  return <FileText size={16} />;
};

export const Expertise = () => {
  const [active, setActive] = useState(null); // sample object

  return (
    <section id="work" data-testid="work-section" className="mx-auto max-w-[1500px] px-6 md:px-12 py-24 md:py-40">
      <Reveal className="max-w-3xl">
        <p className="overline text-terracotta">(03) — Instructional Design</p>
        <h2 className="mt-6 font-serif text-5xl md:text-7xl tracking-tight leading-[0.95] text-ink">
          Selected work,
          <br />
          up close.
        </h2>
      </Reveal>

      {/* Proprietary note */}
      <Reveal delay={0.1}>
        <div className="mt-12 flex flex-col md:flex-row items-start gap-5 rounded-2xl border border-line bg-sand p-7 md:p-9">
          <span className="grid place-items-center w-11 h-11 shrink-0 rounded-full bg-ink text-paper">
            <Lock size={18} />
          </span>
          <p className="text-base md:text-lg text-stone leading-relaxed max-w-4xl">
            <span className="text-ink font-medium">A note on confidentiality.</span> The majority of my
            Instructional Design lives inside client LMS platforms and proprietary enterprise systems,
            so it can't be shared publicly. The samples below are the pieces I'm able to show, and
            there's far more work behind them.
          </p>
        </div>
      </Reveal>

      {/* Samples grid */}
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {ID_SAMPLES.map((s, i) => {
          const clickable = s.type !== "doc";
          const Wrapper = clickable ? "button" : "a";
          const wrapperProps = clickable
            ? { onClick: () => setActive(s), type: "button" }
            : { href: s.src, download: true, target: "_blank", rel: "noopener noreferrer" };
          return (
            <Reveal key={s.key} delay={(i % 3) * 0.08}>
              <Wrapper
                {...wrapperProps}
                data-testid={`id-sample-${s.key}`}
                className="group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-line bg-paper text-left hover:-translate-y-1 hover:border-ink/30 transition-[transform,border-color] duration-500"
              >
                {/* Media */}
                <div className="relative aspect-[4/3] overflow-hidden bg-sand">
                  {s.type === "image" && (
                    <img
                      src={s.src}
                      alt={s.title}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}

                  {s.type === "live" && (
                    <div className="absolute inset-0 grid place-items-center bg-ink">
                      <div className="text-center px-6">
                        <span className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-terracotta text-paper group-hover:scale-110 transition-transform duration-500">
                          <Play size={24} className="ml-1" />
                        </span>
                        <p className="mt-4 font-serif italic text-2xl text-paper">Launch the course</p>
                        <p className="mt-1 text-xs uppercase tracking-[0.2em] text-white/50">
                          Interactive · in-browser
                        </p>
                      </div>
                    </div>
                  )}

                  {s.type === "doc" && (
                    s.thumb ? (
                      <>
                        <img
                          src={s.thumb}
                          alt={`${s.title} preview`}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/25 to-transparent" />
                      </>
                    ) : (
                      <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-sand to-line">
                        <FileText size={54} className="text-ink/30" strokeWidth={1} />
                      </div>
                    )
                  )}

                  {/* type badge */}
                  <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-paper/90 backdrop-blur px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-ink">
                    {iconFor(s.type)} {s.kind}
                  </span>
                </div>

                {/* Text */}
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-serif text-2xl tracking-tight text-ink leading-tight">{s.title}</h3>
                  <p className="mt-1 text-xs uppercase tracking-[0.14em] text-stone">{s.meta}</p>
                  <p className="mt-3 text-sm text-stone leading-relaxed flex-1">{s.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-ink group-hover:text-terracotta transition-colors duration-300">
                    {s.type === "doc" ? (
                      <>
                        <Download size={15} /> Download sample
                      </>
                    ) : (
                      <>
                        {s.type === "live" ? "Open interactive demo" : "View full size"}
                        <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </>
                    )}
                  </span>
                </div>
              </Wrapper>
            </Reveal>
          );
        })}
      </div>

      {/* Toolkit moved to About (after Module Pillars) */}

      {/* Lightbox / Course modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="sample-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/85 backdrop-blur-md p-4 md:p-10"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ scale: 0.94, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-6xl overflow-hidden rounded-2xl bg-paper shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-line px-5 py-3">
                <div className="min-w-0">
                  <p className="font-serif text-xl text-ink truncate">{active.title}</p>
                  <p className="text-[0.65rem] uppercase tracking-[0.18em] text-stone truncate">{active.meta}</p>
                </div>
                <button
                  data-testid="sample-modal-close"
                  onClick={() => setActive(null)}
                  className="grid place-items-center w-10 h-10 rounded-full bg-sand text-ink hover:bg-ink hover:text-paper transition-colors duration-300 shrink-0"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {active.type === "live" ? (
                <div className="w-full bg-black" style={{ height: "min(72vh, 720px)" }}>
                  <iframe
                    title={active.title}
                    src={active.src}
                    className="storyline-frame"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="max-h-[80vh] overflow-auto bg-sand">
                  <img src={active.src} alt={active.title} className="w-full h-auto" />
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
